import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { AuthHelper } from "./views/controllers/AuthHelper";
import { ServerConfig } from "./connectors/Config";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "85vw",
  maxWidth: 560,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 2,
  overflow: "auto",
  maxHeight: "90vh",
  p: 3,
};

const CATEGORIES = [
  { value: "tecnico", label: "Problema tecnico" },
  { value: "account", label: "Account / accesso" },
  { value: "fatturazione", label: "Fatturazione" },
  { value: "certificati", label: "Certificati" },
  { value: "corsi", label: "Corsi" },
  { value: "candidature", label: "Candidature" },
  { value: "altro", label: "Altro" },
];

const PRIORITIES = [
  { value: "bassa", label: "Bassa" },
  { value: "media", label: "Media" },
  { value: "alta", label: "Alta" },
  { value: "urgente", label: "Urgente" },
];

const EMPTY_FORM = {
  subject: "",
  category: "tecnico",
  priority: "media",
  body: "",
  requesterPhone: "",
};

const getAttr = (attributes, name) =>
  (attributes || []).find((x) => x.Name === name)?.Value || "";

const CreateTicket = (props) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [requester, setRequester] = useState({
    requesterName: "",
    requesterEmail: "",
    requesterUserId: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null); // { ticketNumber }

  // Precompila i dati dell'utente loggato all'apertura della modale
  useEffect(() => {
    if (!props.modalOpen) return;

    // reset stato ad ogni apertura
    setError("");
    setSuccess(null);

    (async () => {
      try {
        const user = await AuthHelper.getUserFromIdToken();
        const attrs = user?.UserAttributes || [];
        const name =
          getAttr(attrs, "custom:company") ||
          getAttr(attrs, "name") ||
          user?.Username ||
          "";
        setRequester({
          requesterName: name,
          requesterEmail: getAttr(attrs, "email"),
          requesterUserId: getAttr(attrs, "sub") || user?.Username || "",
        });
      } catch (ex) {
        // In caso di errore lasciamo i campi vuoti: l'email resta comunque richiesta
        console.error("[CreateTicket] impossibile recuperare l'utente:", ex);
      }
    })();
  }, [props.modalOpen]);

  const close = () => {
    props.setModalOpen(false);
  };

  const resetAndClose = () => {
    setForm(EMPTY_FORM);
    setSuccess(null);
    setError("");
    close();
  };

  const validate = () => {
    if (!form.subject.trim()) return "Inserisci l'oggetto della richiesta.";
    if (!form.body.trim()) return "Descrivi il problema nel messaggio.";
    if (!requester.requesterEmail.trim())
      return "Email utente non disponibile: impossibile inviare il ticket.";
    return "";
  };

  const sendRequest = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await fetch(ServerConfig.AWS.Serverless.SUPPORT_TICKET_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: form.subject.trim(),
          category: form.category,
          priority: form.priority,
          body: form.body.trim(),
          requesterPhone: form.requesterPhone.trim(),
          requesterName: requester.requesterName,
          requesterEmail: requester.requesterEmail,
          requesterUserId: requester.requesterUserId,
        }),
      });

      let data = null;
      try {
        data = await res.json();
      } catch (e) {
        data = null;
      }

      if (res.ok && data && data.ok) {
        setSuccess({ ticketNumber: data.ticketNumber });
        setForm(EMPTY_FORM); // reset del form dopo il successo
      } else {
        setError(
          (data && data.error) ||
            "Invio non riuscito. Riprova tra qualche istante."
        );
      }
    } catch (ex) {
      console.error("[CreateTicket] errore invio ticket:", ex);
      setError("Errore di rete. Controlla la connessione e riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={props.modalOpen}
      onClose={close}
      aria-labelledby="ticket-modal-title"
    >
      <Box sx={modalStyle}>
        <Typography id="ticket-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
          Assistenza — Apri un ticket
        </Typography>

        {success ? (
          // --- Schermata di conferma ---
          <Stack spacing={2}>
            <Alert severity="success">
              Ticket inviato correttamente!
              {success.ticketNumber ? (
                <>
                  {" "}
                  Il numero della tua richiesta è{" "}
                  <strong>{success.ticketNumber}</strong>. Ti risponderemo via
                  email all'indirizzo <strong>{requester.requesterEmail}</strong>.
                </>
              ) : (
                " Ti risponderemo al più presto via email."
              )}
            </Alert>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => setSuccess(null)}>
                Nuovo ticket
              </Button>
              <Button variant="contained" onClick={resetAndClose}>
                Chiudi
              </Button>
            </Stack>
          </Stack>
        ) : (
          // --- Form ---
          <Stack spacing={2}>
            {error ? <Alert severity="error">{error}</Alert> : null}

            <TextField
              size="small"
              label="Richiedente"
              value={requester.requesterName}
              InputProps={{ readOnly: true }}
              helperText="Rilevato automaticamente dal tuo account"
              fullWidth
            />
            <TextField
              size="small"
              label="Email di risposta"
              value={requester.requesterEmail}
              InputProps={{ readOnly: true }}
              fullWidth
            />

            <TextField
              required
              label="Oggetto"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              fullWidth
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                select
                label="Categoria"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                fullWidth
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Priorità"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                fullWidth
              >
                {PRIORITIES.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <TextField
              label="Telefono (opzionale)"
              value={form.requesterPhone}
              onChange={(e) =>
                setForm({ ...form, requesterPhone: e.target.value })
              }
              fullWidth
            />

            <TextField
              required
              label="Messaggio"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              multiline
              minRows={4}
              maxRows={8}
              fullWidth
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={close} disabled={loading}>
                Annulla
              </Button>
              <LoadingButton
                variant="contained"
                onClick={sendRequest}
                loading={loading}
              >
                Invia ticket
              </LoadingButton>
            </Stack>
          </Stack>
        )}
      </Box>
    </Modal>
  );
};

export { CreateTicket };
